import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../../lib/utils";

const buttonVariants = {
  base: "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  variants: {
    default: "bg-brand-primary text-white hover:bg-brand-primary/90",
    outline:
      "border border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800",
    secondary:
      "bg-slate-100 text-slate-900 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700",
    ghost:
      "hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-slate-100",
    destructive:
      "bg-destructive text-destructive-foreground hover:bg-destructive/90",
    link: "text-brand-primary underline-offset-4 hover:underline",
  },
  // Default sizing the base intentionally lacked — without it a caller passing
  // only e.g. `rounded-full` rendered with the label jammed against the edges
  // (the cramped DialogFooter buttons). Callers needing a different footprint
  // override the `height`/`padding` groups: a `h-*`/`p-*`/`px-*`/`py-*` in the
  // caller className wins via tailwind-merge in `cn`. NOTE: a `min-h-*` is a
  // DIFFERENT group from `h-*`, so it does NOT override the default `h-10` — it
  // coexists (max wins); pass `h-auto` to opt a compact pill out of `h-10`.
  // `sm` (36px) and `icon` (40px) are below the 44px touch-target bar — add a
  // `min-h-[44px]` companion when using them on a primary touch surface.
  sizes: {
    default: "h-10 px-4 py-2",
    sm: "h-9 px-3 text-xs",
    lg: "h-11 px-6",
    icon: "h-10 w-10",
  },
};

const Button = React.forwardRef(
  ({ className, variant = "default", size = "default", asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants.base,
          buttonVariants.variants[variant] ?? buttonVariants.variants.default,
          buttonVariants.sizes[size] ?? buttonVariants.sizes.default,
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = "Button";

export { Button };
